/** Shared labels for the delivery queue. */

export const STATUS_LABELS: Record<string, string> = {
  PENDING: "Waiting for an agent",
  ASSIGNED: "Out for delivery",
  DELIVERED: "Delivered",
  FAILED: "Attempt failed",
};

/**
 * Why this task exists, in the words of the person carrying it. A delivery
 * agent does not think in order types - they think "new battery" or "their
 * own one back".
 */
export function carryingLabel(task: {
  carrying: "ORDER" | "SERVICE_JOB";
  order: { type: string; serviceJob: { reference: string } | null } | null;
  serviceJob: { outcome: string | null } | null;
}): string {
  if (task.carrying === "SERVICE_JOB") {
    // "repaired" was wrong, and not just loosely: nothing is repaired
    // anywhere in this system. A job that comes back on its OWN battery
    // ended in OK - the battery was found to be fine - or REJECTED, where
    // nothing could be done for it. Neither is a repair, and telling an
    // agent a battery was repaired invites them to say so to the customer.
    return task.serviceJob?.outcome === "REJECTED"
      ? "Their own battery back — nothing could be done"
      : "Their own battery back — tested and fine";
  }
  if (task.order?.serviceJob) {
    // Not "under warranty": warranty is not judged in this app any more, and
    // a PRORATA replacement is one the customer part-paid for. An agent
    // carrying a pro-rata battery told it was a warranty replacement has the
    // wrong story for the doorstep.
    return task.order.type === "PRORATA"
      ? `Part-paid replacement — for ${task.order.serviceJob.reference}`
      : `Free replacement — for ${task.order.serviceJob.reference}`;
  }
  return task.order?.type === "FOC" ? "Free of charge" : "New goods";
}

/**
 * What the agent should be looking for, when the address is a line of free
 * text - which half of the imported customer list is. The coordinates from
 * the most recent capture are often the only thing that will find the place.
 */
export function mapLink(customer: {
  lastGeoLat: string | null;
  lastGeoLong: string | null;
}): string | undefined {
  if (!customer.lastGeoLat || !customer.lastGeoLong) return undefined;
  return `https://www.google.com/maps/search/?api=1&query=${customer.lastGeoLat},${customer.lastGeoLong}`;
}

/**
 * Whether part of the bill has already been collected in the field.
 *
 * Compared as NUMBERS. Both figures arrive as decimal strings but from
 * different places - outstandingAmount is the server's toFixed(2), the bill
 * total is whatever the driver serialised - so "2454.40" and "2454.4" are the
 * same money and different strings. The string comparison told an agent
 * "part already collected" on a bill nobody had paid a rupee of, which is
 * exactly the sort of thing that gets a customer asked for the wrong amount.
 * Found by driving the queue in a browser.
 */
export function partlyCollected(task: {
  outstandingAmount: string;
  order: { bill: { totalAmount: string } | null } | null;
}): boolean {
  const total = task.order?.bill?.totalAmount;
  if (total === undefined || total === null) return false;
  return Math.abs(Number(total) - Number(task.outstandingAmount)) >= 0.005;
}
